/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  DatePicker,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import {
  getTiposDeMovimientoDePersonal,
  getTipoDeMovimientoDePersonal,
  postTipoDeMovimientoDePersonal,
  patchTipoDeMovimientoDePersonal,
  deleteTipoDeMovimientoDePersonal,
  autorizarTipoDeMovimientoDePersonal,
} from 'api/catalogos/tiposDeMovimientoDePersonal';
import { checkToken } from 'api/utils';
import {
  ESTATUS_ACTIVO,
  ESTATUS_INACTIVO,
  ESTATUS_AUTORIZADO,
} from 'utils/constants';

export const tipodemovimientodepersonalPermission = {
  permissionModel: 'tipodemovimientodepersonal',
  catalogsModels: [],
  tabsModels: [],
};

const TiposDeMovimientosDePersonal = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [visibleConfirmInactivo, setVisibleConfirmInactivo] = useState(false);
  const [disableFields, setDisableFields] = useState(false);

  const formInstances = [form];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getTiposDeMovimientoDePersonal();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [_estados, _data] = await Promise.all([
        getEstadosGlobales(),
        getTiposDeMovimientoDePersonal(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _tipo = await getTipoDeMovimientoDePersonal(match.id);
    setTimeout(() => {
      if (_tipo) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_tipo));
          setDisableFields(_tipo?.estados_globales !== ESTATUS_ACTIVO);
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      const saved = await (key
        ? patchTipoDeMovimientoDePersonal(key, values)
        : postTipoDeMovimientoDePersonal(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteTipoDeMovimientoDePersonal(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    form.resetFields();
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar un registro ya autorizado');
        return;
      }
      const saved = await autorizarTipoDeMovimientoDePersonal(key);
      if (saved) {
        const _data = await getTiposDeMovimientoDePersonal();
        setData(formatReceived(_data));
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const TIPO_MOV_LABELS = { UNICO: 'Único Movimiento', DOBLE: 'Movimiento Doble' };
  const MODELO_LABELS = {
    ASIGNACION_PLAZA: 'Asignación de Plaza',
    HISTORIAL_SUELDO: 'Plazas (Histórico de Sueldos)',
    EMPLEADO: 'Empleados',
    INCIDENCIA: 'Registro de Incidencias',
  };

  const columns = [
    {
      titleText: 'Concepto',
      dataIndex: 'concepto',
      key: 'concepto',
      width: 300,
    },
    {
      titleText: 'Tipo de Movimiento',
      dataIndex: 'tipo_de_movimiento',
      key: 'tipo_de_movimiento',
      width: 180,
      render: (val) => TIPO_MOV_LABELS[val] || val,
    },
    {
      titleText: 'Modelo Origen',
      dataIndex: 'modelo_origen',
      key: 'modelo_origen',
      width: 220,
      render: (val) => MODELO_LABELS[val] || val,
    },
    {
      titleText: 'Vigencia Final',
      dataIndex: 'vigencia_final',
      key: 'vigencia_final',
      width: 150,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 150,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    concepto: [requiredRule],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          customActions={[
            {
              onClick: () => setVisibleAutorizar(true),
              icon: <CheckCircleOutlined />,
              text: 'Autorizar',
              disabledBySelection: true,
              disabled: selectedData?.estados_globales !== ESTATUS_ACTIVO,
              className: 'success',
            },
          ]}
          disableDelete={selectedData?.estados_globales === ESTATUS_AUTORIZADO}
          permission={permission}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Tipo de Movimiento de Personal`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableFields}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
            initialValues={{
              estados_globales: ESTATUS_ACTIVO,
            }}
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="concepto"
                rules={rules.concepto}
                label="Concepto"
                hasFeedback
              >
                <Input
                  allowClear
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="tipo_de_movimiento"
                label="Tipo de Movimiento"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  labelProp="label"
                  keyProp="value"
                  dataSource={[
                    { value: 'UNICO', label: 'Único Movimiento' },
                    { value: 'DOBLE', label: 'Movimiento Doble' },
                  ]}
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="modelo_origen"
                label="Modelo Origen"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  labelProp="label"
                  keyProp="value"
                  dataSource={[
                    { value: 'ASIGNACION_PLAZA', label: 'Asignación de Plaza' },
                    { value: 'HISTORIAL_SUELDO', label: 'Plazas (Histórico de Sueldos)' },
                    { value: 'EMPLEADO', label: 'Empleados' },
                    { value: 'INCIDENCIA', label: 'Registro de Incidencias' },
                  ]}
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="vigencia_final"
                label="Vigencia Final"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  disabled={disableFields}
                  format="DD/MM/YYYY"
                  placeholder="Seleccione una fecha"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={estados}
                  activeInactive={selectedData?.estados_globales === ESTATUS_ACTIVO}
                  disabled={selectedRowKeys.length <= 0 || disableFields}
                  onChange={(val) => setVisibleConfirmInactivo(val === ESTATUS_INACTIVO)}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Tipo de Movimiento de Personal: ${form.getFieldValue('concepto')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle={`Autorizar el Tipo de Movimiento de Personal: ${selectedData?.concepto}`}
          open={visibleAutorizar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => setVisibleConfirmInactivo(false)}
          ConfirmInactive
          onCancel={() => {
            form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
            setVisibleConfirmInactivo(false);
          }}
          open={visibleConfirmInactivo}
          contentTitle={`Inactivar el Tipo de Movimiento de Personal: ${selectedData?.concepto}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default TiposDeMovimientosDePersonal;
